// Per-user daily activity timeline (admin only). One day → each worker's chronological
// activity blocks (P&P, fixing, returns, WGR…) with a day summary. New activity sources
// from the backend appear here automatically — no change needed.
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch, API } from '../api';
import { fmtTime, fmtDur } from './shared';

interface Activity {
  type: string;
  source: string;
  label: string;
  start: string | null;
  end: string | null;
  duration_min: number;
  qty: number | null;
  unit: string;
  items: number | null;
  speed: number | null;
  solving: number | null;
  /** P&P blocks: pieces scanned / packed with the Pack button (2026-09-28) */
  scans?: number;
  manual?: number;
}
interface ByType { label: string; blocks: number; qty: number; minutes: number; }
interface Worker {
  username: string;
  display_name: string;
  activities: Activity[];
  summary: {
    active_min: number; span_min: number | null; blocks: number;
    first_start: string | null; last_end: string | null;
    by_type: Record<string, ByType>;
  };
}

const TYPE_COLOR: Record<string, string> = {
  pp:                'bg-blue-900/60 text-blue-300',
  fix:               'bg-orange-900/60 text-orange-300',
  returns_accept:    'bg-purple-900/60 text-purple-300',
  returns_polish:    'bg-teal-900/60 text-teal-300',
  returns_replenish: 'bg-cyan-900/60 text-cyan-300',
  wgr_count:         'bg-emerald-900/60 text-emerald-300',
  product_edit:      'bg-amber-900/60 text-amber-300',
};

function todayISO() {
  // Local calendar date of the browser; the backend resolves warehouse-local if omitted,
  // but we seed a concrete value so the picker is populated.
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function qtyLabel(a: Activity) {
  if (a.qty == null) return '—';
  const main = `${a.qty % 1 === 0 ? a.qty : a.qty.toFixed(1)} ${a.unit}`;
  if (a.items != null && a.unit !== 'items') return `${main} · ${a.items % 1 === 0 ? a.items : a.items.toFixed(1)} pcs`;
  return main;
}

export default function PerUserTab() {
  const [date, setDate]       = useState<string>(todayISO());
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [picked, setPicked]   = useState<string>('all');
  const [loading, setLoading] = useState(false);

  const shiftDay = (delta: number) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const data = await authFetch(`${API}/statistics/user-activity?date=${date}`).then(r => r.json());
      setWorkers(data.workers || []);
    } catch (e) {
      console.error('user-activity load error:', e);
      setWorkers([]);
    }
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const shown = picked === 'all' ? workers : workers.filter(w => w.username === picked);

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button onClick={() => shiftDay(-1)} className="px-2.5 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">←</button>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="bg-gray-800 text-gray-200 text-sm rounded px-2 py-1.5 border border-gray-700 focus:outline-none focus:border-blue-500" />
        <button onClick={() => shiftDay(1)} className="px-2.5 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">→</button>
        <button onClick={() => setDate(todayISO())} className="px-3 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">Today</button>
        <select value={picked} onChange={e => setPicked(e.target.value)}
          className="ml-1 bg-gray-800 text-gray-200 text-sm rounded px-2 py-1.5 border border-gray-700 focus:outline-none focus:border-blue-500">
          <option value="all">All workers</option>
          {workers.map(w => <option key={w.username} value={w.username}>{w.display_name}</option>)}
        </select>
        {loading && <span className="text-gray-600 text-sm ml-1">Loading…</span>}
      </div>

      {shown.length === 0 ? (
        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-12 text-center text-gray-600 text-sm">
          No tracked activity for this day
        </div>
      ) : (
        <div className="space-y-5">
          {shown.map(w => (
            <div key={w.username} className="bg-[#1a1d27] rounded-xl border border-gray-800">
              {/* Worker header + summary */}
              <div className="px-4 py-3 border-b border-gray-800 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-white">{w.display_name}</h2>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2 py-1 rounded-lg bg-gray-800 text-gray-300">
                    Active <span className="text-white font-semibold">{fmtDur(w.summary.active_min)}</span>
                  </span>
                  {w.summary.span_min != null && (
                    <span className="px-2 py-1 rounded-lg bg-gray-800 text-gray-300">
                      Span <span className="text-white font-semibold">{fmtDur(w.summary.span_min)}</span>
                    </span>
                  )}
                  {w.summary.first_start && (
                    <span className="px-2 py-1 rounded-lg bg-gray-800 text-gray-400">
                      {fmtTime(w.summary.first_start)} – {fmtTime(w.summary.last_end)}
                    </span>
                  )}
                  <span className="px-2 py-1 rounded-lg bg-gray-800 text-gray-400">{w.summary.blocks} blocks</span>
                </div>
              </div>

              {/* By-type chips */}
              <div className="px-4 pt-3 flex flex-wrap gap-2">
                {Object.entries(w.summary.by_type).map(([t, v]) => (
                  <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLOR[t] || 'bg-gray-800 text-gray-300'}`}>
                    {v.label}: {v.qty % 1 === 0 ? v.qty : v.qty.toFixed(1)} · {fmtDur(v.minutes)}
                  </span>
                ))}
              </div>

              {/* Activity timeline */}
              <div className="overflow-x-auto p-1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-800">
                      <th className="px-3 py-2 text-left font-medium">Activity</th>
                      <th className="px-3 py-2 text-left font-medium">Start</th>
                      <th className="px-3 py-2 text-left font-medium">End</th>
                      <th className="px-3 py-2 text-right font-medium">Duration</th>
                      <th className="px-3 py-2 text-right font-medium">Quantity</th>
                      <th className="px-3 py-2 text-right font-medium">Speed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w.activities.map((a, i) => (
                      <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors">
                        <td className="px-3 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLOR[a.type] || 'bg-gray-800 text-gray-300'}`}>
                            {a.label}
                          </span>
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-600">{a.source}</span>
                          {a.solving ? <span className="ml-2 text-orange-400 text-xs">+{a.solving} solving</span> : null}
                          {a.type === 'pp' && (a.scans || a.manual) ? (
                            <span className="ml-2 text-xs text-gray-500">{a.scans ?? 0} scanned{a.manual ? <span className="text-amber-400"> · {a.manual} manual</span> : null}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2.5 text-gray-400">{fmtTime(a.start)}</td>
                        <td className="px-3 py-2.5 text-gray-400">{fmtTime(a.end)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-400">{fmtDur(a.duration_min)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-100">{qtyLabel(a)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-300">
                          {a.speed != null ? `${a.speed}/hr` : <span className="text-gray-700">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
